import { Badge } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import type { DomainHealth } from '@/lib/types';
import {
  HEALTH_FIX,
  HEALTH_RECORDS,
  HEALTH_STATUS_LABEL,
  HEALTH_TONE,
  healthResultOf,
} from './email-account-rules';

/**
 * SPF / DKIM / DMARC for one sending domain, record by record.
 *
 * ⚠️ **Warn-only, by owner decision.** A failing record does not stop the
 * account sending; this panel reports, it does not gate. So nothing here is
 * worded as a refusal.
 */
export function DomainHealthPanel({
  health,
  checkedAt,
}: {
  health: DomainHealth;
  checkedAt: string | null;
}) {
  return (
    <div className="grid min-w-0 gap-3">
      <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        Domain <span className="font-medium text-fg-secondary">{health.domain}</span>
        {checkedAt ? <> · checked {formatDateTime(checkedAt)}</> : null}
      </p>
      <ul className="m-0 grid list-none gap-3 p-0">
        {HEALTH_RECORDS.map(({ key, label }) => {
          const result = healthResultOf(health, key);
          return (
            <li key={key} className="min-w-0 rounded-md border border-border bg-surface-sunken p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-fg">{label}</span>
                <Badge tone={HEALTH_TONE[result.status]} dot>
                  {HEALTH_STATUS_LABEL[result.status]}
                </Badge>
              </div>
              <p className="mt-1.5 mb-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
                {result.detail}
              </p>
              <div className="mt-2">
                <p className="m-0 text-xs font-medium text-fg-muted">
                  Published record
                </p>
                {result.record === null ? (
                  <p className="mt-0.5 mb-0 text-xs text-fg-subtle">None found</p>
                ) : (
                  <code className="mt-0.5 block rounded-sm border border-border bg-surface px-1.5 py-1 text-[11px] text-fg-secondary [overflow-wrap:anywhere]">
                    {result.record}
                  </code>
                )}
              </div>
              {result.status !== 'pass' && (
                <p className="mt-2 mb-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
                  <span className="font-medium text-fg">To fix: </span>
                  {HEALTH_FIX[key]}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Three compact badges for a list row; detail on each `title`. */
export function DomainHealthBadges({ health }: { health: DomainHealth | null }) {
  if (health === null) return <span className="text-xs text-fg-subtle">Not checked</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {HEALTH_RECORDS.map(({ key, label }) => {
        const result = healthResultOf(health, key);
        return (
          <Badge
            key={key}
            tone={HEALTH_TONE[result.status]}
            title={`${label}: ${HEALTH_STATUS_LABEL[result.status]} — ${result.detail}`}
          >
            {label}
          </Badge>
        );
      })}
    </div>
  );
}
